import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import { useProjects } from '../hooks/useProjects';

// 크로스 오리진 URL도 강제 다운로드 (Blob 방식)
async function triggerDownload(url) {
  try {
    const res  = await fetch(url);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    // 파일명: URL 마지막 세그먼트에서 타임스탬프 접두사 제거
    const raw  = decodeURIComponent(url.split('/').pop().split('?')[0]);
    const name = raw.replace(/^\d{13}-/, '');
    const a    = document.createElement('a');
    a.href     = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    // CORS 차단 등 실패 시 새 탭 폴백
    window.open(url, '_blank');
  }
}

const TABS = [
  { key: 'all',      label: 'All'     },
  { key: 'writing',  label: 'Writing'  },
  { key: 'download', label: 'Assets'   },
];

/* ── Writing card ─────────────────────────────────────────────────────────── */
function WritingCard({ item }) {
  const year     = item.year || '';
  const category = item.role || 'Writing';
  const image    = item.images?.[0]?.src || item.thumbnail_url || null;

  return (
    <Link to={`/log/${item.id}`} className="work-card-link">
      <div className="work-card">
        <div className="work-card__img">
          {image
            ? <img src={image} alt={item.title} loading="lazy" />
            : <div style={{ width: '100%', height: '100%', background: 'var(--c-n100)' }} />}
        </div>
        <h3 className="work-card__title">{item.title}</h3>
        <div className="work-card__meta">
          <div className="work-card__meta-left">
            {year && <span className="work-card__year">{year}</span>}
            <span className="work-card__type">{category}</span>
          </div>
          {item.tags && item.tags.length > 0 && (
            <div className="work-card__tags work-card__tags--hidden">
              {item.tags.slice(0, 2).map(tag => (
                <span key={tag} className="work-card__tag">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ── Asset / Download card ────────────────────────────────────────────────── */
function AssetCard({ item }) {
  const [downloading, setDownloading] = useState(false);

  const href        = item.externalUrl || '#';
  const format      = item.role || 'File';
  const preview     = item.images?.[0]?.src || item.thumbnail_url || null;
  const hasUrl      = !!item.externalUrl;
  const forDownload = hasUrl && item.linkType !== 'link';

  const tagLabel = !hasUrl
    ? 'No link'
    : downloading
      ? '다운로드 중…'
      : forDownload ? '↓ Download' : '↗ Open';

  const handleClick = (e) => {
    if (!hasUrl) { e.preventDefault(); return; }
    if (forDownload) {
      e.preventDefault();
      setDownloading(true);
      triggerDownload(href).finally(() => setDownloading(false));
    }
    // linkType === 'link' 이면 기본 <a> 동작 (새 탭) 그대로
  };

  return (
    <a
      href={href}
      target={forDownload ? undefined : '_blank'}
      rel={forDownload ? undefined : 'noopener noreferrer'}
      className="work-card-link"
      onClick={handleClick}
      style={(!hasUrl || downloading) ? { cursor: downloading ? 'wait' : 'default', opacity: downloading ? 0.7 : 0.6 } : undefined}
    >
      <div className="work-card">
        <div className="work-card__img">
          {preview
            ? <img src={preview} alt={item.title} loading="lazy" />
            : <div style={{ width: '100%', height: '100%', background: 'var(--c-n100)' }} />}
        </div>
        <h3 className="work-card__title">{item.title}</h3>
        <div className="work-card__meta">
          <div className="work-card__meta-left">
            {item.year && <span className="work-card__year">{item.year}</span>}
            <span className="work-card__type">{format}</span>
          </div>
          <div className="work-card__tags">
            <span className={`work-card__tag${forDownload ? '' : ' work-card__tag--hidden'}`}>{tagLabel}</span>
          </div>
        </div>
      </div>
    </a>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */
export default function LogPage() {
  const [tab, setTab] = useState('all');

  const { projects: writings,  loading: wLoading  } = useProjects('log');
  const { projects: downloads, loading: dLoading  } = useProjects('download');

  const all = [
    ...writings.map(item => ({ item, kind: 'writing' })),
    ...downloads.map(item => ({ item, kind: 'download' })),
  ].sort((a, b) => (b.item.year || '').localeCompare(a.item.year || ''));

  const loading = tab === 'all' ? (wLoading || dLoading) : tab === 'writing' ? wLoading : dLoading;
  const items   = tab === 'all' ? all : tab === 'writing' ? writings : downloads;

  return (
    <PageLayout>
      <div className="work-page">
        <div className="work-title-area">
          <h1 className="work-h1">Log</h1>
        </div>

        {/* 탭 바 */}
        <div className="work-filter-bar">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`work-filter-btn${tab === key ? ' work-filter-btn--active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 그리드 */}
        <div className="work-grid">
          {loading ? (
            <div style={{ gridColumn: '1/-1', color: 'var(--c-n400)', fontSize: '0.875rem', padding: '2rem 0' }}>
              불러오는 중…
            </div>
          ) : items.length === 0 ? (
            <div style={{ gridColumn: '1/-1', color: 'var(--c-n400)', fontSize: '0.875rem', padding: '2rem 0' }}>
              항목이 없습니다.
            </div>
          ) : tab === 'all' ? (
            all.map(({ item, kind }) => (
              kind === 'writing'
                ? <WritingCard key={`w-${item.id}`} item={item} />
                : <AssetCard key={`d-${item.id}`} item={item} />
            ))
          ) : tab === 'writing' ? (
            writings.map(item => <WritingCard key={item.id} item={item} />)
          ) : (
            downloads.map(item => <AssetCard key={item.id} item={item} />)
          )}
        </div>
      </div>

      <Link to="/contact" className="float-contact" aria-label="Contact">
        Contact
      </Link>
    </PageLayout>
  );
}
